import { useEffect, useState } from "react";
import { FileSearch, Check, X, Loader2, Phone, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { DEMO_SUBPOENAS, DEMO_SUBPOENA_RECORDS } from "./mdtDemo";
import { inputCls, btnCls, toastOk } from "./RosterTab";

/* Subpoenas — Sgt+ requests a citizen's phone records; command unseals them. */

const STATUS = { pending: "text-amber-300 border-amber-500/40", approved: "text-emerald-300 border-emerald-500/40", denied: "text-red-300 border-red-500/40" };

export default function SubpoenasTab({ G }) {
  const { serverId, token, demo, sfx, me } = G;
  const [subs, setSubs] = useState(null);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [records, setRecords] = useState(null); // {subId, data}
  const [busy, setBusy] = useState("");

  const load = () => {
    if (demo) { setSubs(DEMO_SUBPOENAS); return; }
    api.get(`/nc/servers/${serverId}/cad/dept/subpoenas`, { params: { token } })
      .then((r) => setSubs(r.data.subpoenas)).catch(() => setSubs([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!/^\d+$/.test(target.trim()) || reason.trim().length < 10) return;
    sfx.keyTick();
    if (demo) {
      setSubs((s) => [{ id: `l${Date.now()}`, target_name: `#${target}`, target_playercode: +target, reason: reason.trim(), requester_name: me.name, status: "pending", at: new Date().toISOString() }, ...s]);
      setTarget(""); setReason(""); return;
    }
    setBusy("new");
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/dept/subpoenas`, { target_playercode: +target, reason: reason.trim() }, { params: { token } });
      toastOk(r.data.message); setTarget(""); setReason(""); load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(""); }
  };
  const decide = async (s, approve) => {
    sfx.keyTick();
    if (demo) { setSubs((x) => x.map((y) => y.id === s.id ? { ...y, status: approve ? "approved" : "denied", decided_by: me.name } : y)); return; }
    setBusy(s.id);
    try { const r = await api.post(`/nc/servers/${serverId}/cad/dept/subpoenas/${s.id}/decide`, { approve }, { params: { token } }); toastOk(r.data.message); load(); }
    catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(""); }
  };
  const viewRecords = async (s) => {
    sfx.keyTick();
    if (records?.subId === s.id) { setRecords(null); return; }
    if (demo) { setRecords({ subId: s.id, data: DEMO_SUBPOENA_RECORDS }); return; }
    setBusy(s.id);
    try {
      const r = await api.get(`/nc/servers/${serverId}/cad/dept/subpoenas/${s.id}/records`, { params: { token } });
      setRecords({ subId: s.id, data: r.data });
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Sealed")); }
    finally { setBusy(""); }
  };

  if (!subs) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="subpoenas-tab">
      <div className="border border-white/10 bg-black/40 p-3 flex flex-col gap-2">
        <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><FileSearch className="w-3 h-3 text-purple-400" /> Request phone records (Sgt+) — command unseals</p>
        <div className="flex gap-1.5">
          <input data-testid="subpoena-target" value={target} onChange={(e) => setTarget(e.target.value.replace(/\D/g, ""))} placeholder="Citizen playercode" className={`${inputCls} w-36`} />
          <input data-testid="subpoena-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Case justification (min 10 chars)…" className={`${inputCls} flex-1 min-w-0`} />
          <button data-testid="subpoena-submit" onClick={submit} disabled={busy === "new" || !/^\d+$/.test(target.trim()) || reason.trim().length < 10}
            className={`${btnCls} border-purple-500/50 text-purple-300 hover:bg-purple-500/10`}>{busy === "new" ? "…" : "File"}</button>
        </div>
      </div>
      {subs.map((s) => (
        <div key={s.id} data-testid={`subpoena-${s.id.slice(0, 6)}`} className="border border-white/10 bg-black/40 p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-[10px] font-bold text-zinc-100">{s.target_name} <span className="text-zinc-500">#{s.target_playercode}</span></p>
            <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS[s.status]}`}>{s.status}</span>
            <span className="ml-auto font-mono text-[7.5px] text-zinc-600 uppercase">by {s.requester_name}{s.decided_by ? ` · ${s.status} by ${s.decided_by}` : ""}</span>
          </div>
          <p className="font-mono text-[9px] text-zinc-400 leading-snug">{s.reason}</p>
          <div className="flex gap-1.5">
            {me.is_command && s.status === "pending" && (
              <>
                <button data-testid={`subpoena-approve-${s.id.slice(0, 6)}`} disabled={busy === s.id} onClick={() => decide(s, true)} className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1`}><Check className="w-3 h-3" /> Unseal</button>
                <button data-testid={`subpoena-deny-${s.id.slice(0, 6)}`} disabled={busy === s.id} onClick={() => decide(s, false)} className={`${btnCls} border-red-500/50 text-red-300 hover:bg-red-500/10 flex items-center gap-1`}><X className="w-3 h-3" /> Deny</button>
              </>
            )}
            {s.status === "approved" && (
              <button data-testid={`subpoena-records-${s.id.slice(0, 6)}`} disabled={busy === s.id} onClick={() => viewRecords(s)}
                className={`${btnCls} border-purple-500/50 text-purple-300 hover:bg-purple-500/10`}>
                {busy === s.id ? "…" : records?.subId === s.id ? "Hide records" : "View records"}
              </button>
            )}
          </div>
          {records?.subId === s.id && (
            <div className="border border-purple-500/30 bg-purple-500/5 p-2.5 flex flex-col gap-1.5" data-testid="subpoena-records">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-purple-300 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Messages ({records.data.messages.length})</p>
              {records.data.messages.slice(0, 20).map((m, i) => (
                <p key={i} className="font-mono text-[8.5px] text-zinc-300 leading-snug">
                  <span className="text-zinc-500">{String(m.at).slice(5, 16).replace("T", " ")} ·</span> <span className="text-purple-200">{m.from_name || m.from_pc} → {m.to_name || m.to_pc}:</span> {m.body}
                </p>
              ))}
              {records.data.messages.length === 0 && <p className="font-mono text-[8px] text-zinc-500">No messages on file.</p>}
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-purple-300 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> Call log ({records.data.calls.length})</p>
              {records.data.calls.slice(0, 10).map((c, i) => (
                <p key={i} className="font-mono text-[8.5px] text-zinc-400">{String(c.at || c.created_at || "").slice(5, 16).replace("T", " ")} · #{c.caller_pc} → #{c.callee_pc} · {c.status}</p>
              ))}
              {records.data.calls.length === 0 && <p className="font-mono text-[8px] text-zinc-500">No calls on file.</p>}
            </div>
          )}
        </div>
      ))}
      {subs.length === 0 && <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] p-3 text-center">No subpoena requests</p>}
    </div>
  );
}
